import { useApolloClient, useQuery } from '@apollo/client/react';
import type { SyntheticEvent } from 'react';
import { useEffect, useRef } from 'react';

import { buildContentLockReturnTarget, buildPricingHref } from '@repo/common';
import { GetMbaAccessDocument } from '@repo/frontend-shared';

import { useCurrentUser } from '../contexts/current-user-context';
import { type LockedContentKind, useLockedContentUpgrade } from '../contexts/locked-content-upgrade-context';
import { useUi } from '../contexts/ui-context';
import { useLocation, useNavigation } from './use-navigation';

const FEATURE_FLAG_ALIASES: Record<string, string[]> = {
  scenarios_cards: ['scenarios_dashboard_cards'],
};

/**
 * Check if a feature is enabled by feature flag key.
 * Now reads strictly from the UiProvider context.
 * The provider handles the fetching (SSR or CSR fallback).
 *
 * Usage:
 * ```tsx
 * const isEnabled = useFeatureFlag('ai_assistant');
 * ```
 *
 * @param featureFlagKey - feature flag key (e.g., 'search_box')
 * @returns true if feature is enabled
 */
export const useFeatureFlag = (featureFlagKey: string): boolean => {
  const { featureFlags } = useUi();

  const aliasedFlags = FEATURE_FLAG_ALIASES[featureFlagKey] ?? [];
  const featureFlagsSet = new Set(featureFlags);

  return [featureFlagKey, ...aliasedFlags].some(flag => featureFlagsSet.has(flag));
};

interface UseMbaAccessStateResult {
  hasMbaAccess: boolean;
  canAccessPremiumContent: boolean;
  /**
   * The entitlement lookup failed and no cached answer exists. `hasMbaAccess`
   * is then `false` without being known, so offers that must never reach an
   * entitled user (e.g. the Trigger 5 return offer) should stay hidden.
   */
  hasAccessError: boolean;
  isCheckingAccess: boolean;
  isMbaEnabled: boolean;
  shouldShowContextualUpsell: boolean;
  shouldShowDashboardPromo: boolean;
}

/**
 * The user's real MBA entitlement, including an active full-program partner
 * assignment regardless of role or student profile. Billing and upsell decisions depend on
 * this being literally true, so a staff role must never be folded in here —
 * see `useCanAccessPremiumContent` for authorization to open premium content.
 */
export const useMbaAccessState = (): UseMbaAccessStateResult => {
  const isMbaEnabled = useFeatureFlag('mba_certificate');
  const { isLoggedIn } = useCurrentUser();
  const client = useApolloClient();
  const { data, loading, error } = useQuery(GetMbaAccessDocument, {
    fetchPolicy: 'cache-and-network',
    // Feature rollout controls presentation, never an existing entitlement.
    skip: !isLoggedIn,
  });

  const hasMbaAccess = isLoggedIn && data?.currentMbaAccess === true;
  const isCheckingAccess = isLoggedIn && loading;

  // `undefined` until the first settled (non-loading) observation, so the
  // initial mount for an already-entitled user is never mistaken for a
  // transition. Only a genuine false -> true flip (e.g. an MBA purchase
  // completing mid-session) clears the cache — freemium-locked list data
  // cached before entitlement must not survive it.
  const previousHasMbaAccessRef = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (loading) return;
    if (previousHasMbaAccessRef.current === false && hasMbaAccess) {
      client.resetStore().catch(error => {
        console.error('Failed to reset Apollo cache after MBA access change:', error);
      });
    }
    previousHasMbaAccessRef.current = hasMbaAccess;
  }, [hasMbaAccess, loading, client]);

  return {
    hasMbaAccess,
    canAccessPremiumContent: isLoggedIn && data?.currentPremiumContentAccess === true,
    hasAccessError: isLoggedIn && Boolean(error) && data?.currentMbaAccess === undefined,
    isCheckingAccess,
    isMbaEnabled,
    shouldShowContextualUpsell: isMbaEnabled && !loading && !hasMbaAccess,
    shouldShowDashboardPromo: isMbaEnabled && !loading,
  };
};

/**
 * Reads the backend's global premium-content capability. Resource-specific
 * partner grants still come from each resource's backend lock verdict.
 *
 * This is deliberately separate from `useMbaAccessState`. Entitlement answers
 * "is this person an MBA subscriber?", which billing and subscription UI render
 * as account state; authorization answers "may this person open the content?".
 * Administrators are authorized without being subscribers, mirroring the
 * backend's `canAccessContentTier`, which grants ADMIN and SUPER_ADMIN before it
 * looks at MBA entitlement. No subscription is created and no account data
 * changes.
 */
export const useCanAccessPremiumContent = (): boolean => {
  return useMbaAccessState().canAccessPremiumContent;
};

interface UseContentLockStateOptions {
  contentTitle?: string;
  /** Set for templates and deals: activation opens the contextual upgrade modal when one is mounted. */
  contentKind?: LockedContentKind;
}

interface UseContentLockStateResult {
  isLocked: boolean;
  onLockedActivate: (event: SyntheticEvent) => void;
}

/**
 * The control the user activated, so the upgrade modal can hand focus back to
 * it. Prefers the focused element inside the handler's element (keyboard
 * activation of an inner button), else the handler's element itself.
 */
function resolveActivationOpener(event: SyntheticEvent): HTMLElement | null {
  const { currentTarget } = event;
  if (!(currentTarget instanceof HTMLElement)) return null;
  const active = typeof document === 'undefined' ? null : document.activeElement;
  return active instanceof HTMLElement && currentTarget.contains(active) ? active : currentTarget;
}

/**
 * Renders the backend's lock verdict and provides a handler that stops the
 * card's default navigation. Templates and
 * deals (`contentKind`) open the contextual upgrade modal when the app mounts
 * `LockedContentUpgradeProvider`; everything else routes to pricing. Locked
 * rows still resolve card-safe fields from the backend (freemium discovery) —
 * only the click-through is gated here.
 *
 * Authorization comes entirely from the resource's backend verdict. Account
 * roles, feature flags and cached MBA entitlement cannot override it.
 */
export const useContentLockState = (
  isLocked: boolean,
  options: UseContentLockStateOptions = {},
): UseContentLockStateResult => {
  const { navigate } = useNavigation();
  const location = useLocation();
  const lockedContentUpgrade = useLockedContentUpgrade();

  const onLockedActivate = (event: SyntheticEvent): void => {
    event.stopPropagation();
    event.preventDefault();
    const { returnTo, returnOrigin } = buildContentLockReturnTarget(
      location.pathname,
      typeof window !== 'undefined' ? window.location.search : '',
      typeof window !== 'undefined' ? window.location.hostname : undefined,
    );
    if (lockedContentUpgrade && options.contentKind && options.contentTitle) {
      lockedContentUpgrade.openLockedContentUpgrade({
        kind: options.contentKind,
        title: options.contentTitle,
        returnTo,
        returnOrigin,
        opener: resolveActivationOpener(event),
      });
      return;
    }
    navigate(
      buildPricingHref({
        returnTo,
        returnOrigin,
        courseTitle: options.contentTitle,
      }),
    );
  };

  return { isLocked, onLockedActivate };
};
